<template>
  <v-dialog
    :model-value="modelValue"
    max-width="520"
    :scrim="true"
    class="report-issue-overlay"
    @update:model-value="onDialogToggle">
    <v-card class="report-issue">
      <template v-if="!submitted">
        <v-card-title class="report-issue__title">Report an Issue</v-card-title>
        <v-card-text class="report-issue__body">
          <p v-if="orderKey" class="report-issue__message">
            Tell us what's wrong with submission <span class="report-issue__key">{{ orderKey }}</span> and our team will
            take a look.
          </p>
          <p v-else class="report-issue__message">
            Spotted a problem? Tell us what's wrong and our team will take a look — we'll note the page you're on.
          </p>

          <div class="report-issue__section-label">What's the issue?</div>
          <div class="report-issue__categories">
            <button
              v-for="option in categories"
              :key="option.value"
              type="button"
              class="report-issue__chip"
              :class="{ 'report-issue__chip--selected': category === option.value }"
              @click="category = option.value">
              {{ option.label }}
            </button>
          </div>

          <v-textarea
            v-model="description"
            label="Describe the issue (optional)"
            variant="outlined"
            rows="3"
            auto-grow
            maxlength="2000"
            class="mt-4"
            hide-details="auto" />

          <div class="report-issue__attach-note">
            <v-icon size="14" class="mr-1">mdi-camera-outline</v-icon>
            A screenshot of this page and its URL will be attached.
          </div>

          <v-alert v-if="errorMsg" type="error" variant="tonal" density="compact" class="mt-4">
            {{ errorMsg }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="report-issue__actions">
          <v-btn
            v-if="dashboardEnabled"
            variant="text"
            size="small"
            class="report-issue__dashboard-link"
            prepend-icon="mdi-ticket-confirmation-outline"
            @click="goToReportedIssues">
            View your reported issues
          </v-btn>
          <v-spacer />
          <v-btn variant="text" :disabled="loading" @click="onCancel">Cancel</v-btn>
          <v-btn color="primary" :loading="loading" :disabled="!category" @click="onSubmit">Send Report</v-btn>
        </v-card-actions>
      </template>

      <template v-else>
        <v-card-text class="report-issue__success">
          <div class="report-issue__success-icon">
            <v-icon size="28">mdi-check</v-icon>
          </div>
          <div class="report-issue__success-title">Report sent</div>
          <div class="report-issue__success-text">Thanks — our team has been notified.</div>
          <v-btn
            v-if="dashboardEnabled"
            variant="text"
            color="primary"
            size="small"
            class="report-issue__dashboard-link mt-3"
            @click="goToReportedIssues">
            View your reported issues
          </v-btn>
        </v-card-text>
      </template>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { orderInfoService } from "@/services/orderInfoService";
import { miscService } from "@/services/miscService";
import { useRequirementsStore } from "@/stores/requirementsStore";

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  orderKey: {
    type: String,
    required: false,
    default: null,
  },
});

const emit = defineEmits(["update:modelValue", "reported"]);

const router = useRouter();
const requirementsStore = useRequirementsStore();
const dashboardEnabled = computed(() => requirementsStore.supportTicketDashboardEnabled === true);

const SUBMISSION_CATEGORIES = [
  { value: "INCORRECT_DATA", label: "Incorrect or missing info" },
  { value: "NOT_PROGRESSING", label: "Stuck / not progressing" },
  { value: "WRONG_STATUS", label: "Status looks wrong" },
  { value: "RESPONSE_ISSUE", label: "Problem with plan response" },
  { value: "OTHER", label: "Other" },
];

const PAGE_CATEGORIES = [
  { value: "PAGE_PROBLEM", label: "Problem with this page" },
  { value: "INCORRECT_DATA", label: "Data looks wrong" },
  { value: "OTHER", label: "Other" },
];

const categories = computed(() => (props.orderKey ? SUBMISSION_CATEGORIES : PAGE_CATEGORIES));

const category = ref(null);
const description = ref("");
const loading = ref(false);
const errorMsg = ref("");
const submitted = ref(false);
let closeTimer = null;
let screenshot = null;

watch(
  () => props.modelValue,
  async (open) => {
    clearCloseTimer();
    if (open) {
      category.value = null;
      description.value = "";
      errorMsg.value = "";
      submitted.value = false;
      captureScreenshot();
      try {
        await requirementsStore.ensureSupportTicketDashboardFlag();
      } catch (e) {
        // Non-fatal: the dashboard link just stays hidden.
      }
    }
  }
);

function clearCloseTimer() {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
}

async function captureScreenshot() {
  screenshot = null;
  try {
    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(document.body, {
      logging: false,
      scale: 1,
      ignoreElements: (el) => el.classList?.contains("v-overlay-container"),
    });
    const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
    // Keep request bodies well under Django's upload limit; drop oversized captures.
    if (dataUrl.length < 3_000_000) screenshot = dataUrl;
  } catch {
    screenshot = null;
  }
}

onBeforeUnmount(clearCloseTimer);

function onDialogToggle(value) {
  if (!loading.value) emit("update:modelValue", value);
}

function onCancel() {
  emit("update:modelValue", false);
}

async function onSubmit() {
  if (!category.value) return;
  loading.value = true;
  errorMsg.value = "";
  try {
    const payload = {
      category: category.value,
      description: description.value.trim(),
      pageUrl: window.location.pathname + window.location.search,
      screenshot: screenshot || "",
    };
    if (props.orderKey) {
      await orderInfoService.reportSubmissionIssue(props.orderKey, payload);
    } else {
      await miscService.reportIssue(payload);
    }
    submitted.value = true;
    emit("reported");
    clearCloseTimer();
    if (!dashboardEnabled.value) {
      closeTimer = setTimeout(() => emit("update:modelValue", false), 1600);
    }
  } catch (e) {
    errorMsg.value = e?.message || "Failed to send the report. Please try again.";
  } finally {
    loading.value = false;
  }
}

function goToReportedIssues() {
  clearCloseTimer();
  emit("update:modelValue", false);
  router.push({ name: "reported-issues" });
}
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.report-issue {
  border-radius: 16px !important;
  box-shadow: $lh_shadow_lg !important;
  overflow: hidden;
}

.report-issue__title {
  font-size: $lh_font_size_large;
  font-weight: $lh_font_weight_bold;
  padding: 20px 24px 8px;
}

.report-issue__body {
  padding: 8px 24px 16px;
}

.report-issue__message {
  font-size: $lh_body3_2_size;
  color: $lh_color_blue_gray;
  line-height: 1.5;
  margin-bottom: 16px;
}

.report-issue__key {
  font-family: $lh_font_mono;
  font-size: 13px;
  color: $lh_color_navy_dark;
  background: rgba($lh_color_navy, 0.06);
  border-radius: 4px;
  padding: 1px 5px;
}

.report-issue__section-label {
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: $lh_color_blue_gray;
  margin-bottom: 10px;
}

.report-issue__categories {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.report-issue__chip {
  font-family: $lh_font_family_dm_sans;
  font-size: 13px;
  font-weight: $lh_font_weight_medium;
  line-height: 1;
  padding: 9px 14px;
  border-radius: 999px;
  border: 1px solid rgba($lh_color_navy, 0.18);
  background: $lh_dash_surface;
  color: $lh_color_navy;
  cursor: pointer;
  transition: background $lh_transition_fast, border-color $lh_transition_fast, color $lh_transition_fast;

  &:hover {
    background: rgba($lh_color_navy, 0.05);
  }

  &--selected {
    background: $lh_color_navy;
    border-color: $lh_color_navy;
    color: #ffffff;

    &:hover {
      background: $lh_color_navy;
    }
  }
}

.report-issue__attach-note {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: $lh_color_blue_gray;
  margin-top: 10px;
}

.report-issue__success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 36px 24px 32px;
}

.report-issue__success-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba($lh_color_green, 0.12);
  color: $lh_color_green;
  margin-bottom: 14px;
}

.report-issue__success-title {
  font-size: $lh_font_size_large;
  font-weight: $lh_font_weight_bold;
  color: $lh_color_navy_dark;
  margin-bottom: 4px;
}

.report-issue__success-text {
  font-size: $lh_body3_2_size;
  color: $lh_color_blue_gray;
}
</style>

<style lang="scss">
.report-issue-overlay .v-overlay__scrim {
  backdrop-filter: blur(4px);
}
</style>
